import React, { useCallback, useEffect, useState } from "react";
import { Button, Popconfirm, Table, Tag, Tooltip } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { deleteSurveyTemplate, getSurveyTemplates } from "./educationApi";
import { getSurveyClientId, getSurveyList, loadSurveyTemplate, notifySurveyResult, scrollToTopOnTableChange, useColumnSearch } from "./surveyUtils";
import { SurveyPreviewDrawer } from "./SurveyDynamicForm";
import SurveyTemplateFormModal from "./SurveyTemplateFormModal";

// Ported from the old app's surveyTemplateBuilder/index.js — the list of
// generic survey templates with preview / edit / delete.
const SurveyTemplates = () => {
  const columnSearch = useColumnSearch();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [modalState, setModalState] = useState({ visible: false, record: null });
  const [preview, setPreview] = useState({ visible: false, loading: false, template: null });

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    const response = await getSurveyTemplates({ client_id: getSurveyClientId() });
    setTemplates(getSurveyList(response));
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const handleDelete = async (record) => {
    setDeletingId(record.id);
    const response = await deleteSurveyTemplate(record.id);
    setDeletingId(null);
    if (notifySurveyResult(response, "Survey template deleted.")) loadTemplates();
  };

  const handlePreview = async (record) => {
    setPreview({ visible: true, loading: true, template: null });
    const template = await loadSurveyTemplate(record.id);
    setPreview((previous) => (previous.visible ? { ...previous, loading: false, template } : previous));
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "name",
      key: "name",
      width: "30%",
      ...columnSearch("name", "Template name"),
    },
    {
      title: "Survey Type",
      dataIndex: "survey_type",
      key: "survey_type",
      width: "20%",
      ...columnSearch("survey_type", "Survey type"),
      render: (value) => {
        if (!value || value === "-") return "-";
        const normalized = value.toLowerCase();
        const color = normalized === "cleaning" ? "blue" : normalized === "laundry" ? "gold" : "purple";
        return <Tag color={color}>{value}</Tag>;
      },
    },
    {
      title: "Questions",
      dataIndex: "question_count",
      key: "question_count",
      width: "12%",
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: "13%",
      align: "center",
      render: (value) => (Number(value) === 1 ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>),
    },
    {
      title: "Action",
      key: "action",
      width: "25%",
      align: "center",
      render: (_, record) => (
        <>
          <Tooltip title="Preview">
            <Button size="small" icon={<EyeOutlined />} aria-label="Preview" onClick={() => handlePreview(record)} style={{ marginRight: 8 }} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button size="small" type="primary" icon={<EditOutlined />} aria-label="Edit" onClick={() => setModalState({ visible: true, record })} style={{ marginRight: 8 }} />
          </Tooltip>
          <Popconfirm title="Are you sure you want to delete this template?" okText="Delete" okType="danger" onConfirm={() => handleDelete(record)}>
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} aria-label="Delete" loading={deletingId === record.id} />
            </Tooltip>
          </Popconfirm>
        </>
      ),
    },
  ];

  const dataSource = templates.map((template) => ({
    ...template,
    name: template.name || "-",
    survey_type: template.survey_type || "-",
    question_count: template.question_count !== undefined ? template.question_count : Array.isArray(template.questions) ? template.questions.length : 0,
  }));

  return (
    <div>
      <div style={{ textAlign: "right", marginBottom: 16 }}>
        <Button type="primary" shape="round" icon={<PlusCircleOutlined />} onClick={() => setModalState({ visible: true, record: null })}>
          New Survey Template
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        locale={{ emptyText: 'No survey templates yet. Use "New Survey Template" to create one.' }}
        onChange={scrollToTopOnTableChange}
      />

      <SurveyTemplateFormModal
        visible={modalState.visible}
        record={modalState.record}
        onClose={() => setModalState({ visible: false, record: null })}
        onSaved={loadTemplates}
      />

      <SurveyPreviewDrawer
        visible={preview.visible}
        loading={preview.loading}
        template={preview.template}
        onClose={() => setPreview({ visible: false, loading: false, template: null })}
      />
    </div>
  );
};

export default SurveyTemplates;
